<template>
    <app-layout>
        <common-page-plain>
            <common-content-spacer height="medium">
                <router-link :to="{ name: 'governance.overview' }" class="governance-detail__back">
                    ← {{ $t('pages.governance.detail.back_to_overview') }}
                </router-link>

                <div v-if="isLoading" class="governance-detail__state">
                    <common-loader :isProcessing="true"></common-loader>
                </div>

                <div v-else-if="hasError" class="governance-detail__state governance-detail__state--error">
                    {{ $t('pages.governance.detail.error') }}
                </div>

                <template v-else-if="broadcast">
                    <common-page-header :heading="`${$t('pages.governance.detail.title')} #${broadcast.id}`">
                        <template #right>
                            <governance-status-badge :status="broadcast.status"></governance-status-badge>
                        </template>
                    </common-page-header>

                    <div class="governance-summary">
                        <div class="governance-summary__item">
                            <p class="governance-summary__label">{{ $t('pages.governance.detail.header.referral') }}</p>
                            <p class="governance-summary__value">#{{ broadcast.referralId }}</p>
                        </div>
                        <div class="governance-summary__item">
                            <p class="governance-summary__label">{{ $t('pages.governance.detail.header.current_round') }}</p>
                            <p class="governance-summary__value">{{ broadcast.currentRound }}</p>
                        </div>
                        <div class="governance-summary__item">
                            <p class="governance-summary__label">{{ $t('pages.governance.detail.header.reserved_provider') }}</p>
                            <p class="governance-summary__value">
                                {{ broadcast.reservedProviderId || $t('pages.governance.detail.header.none') }}
                            </p>
                        </div>
                    </div>

                    <common-buttons class="governance-actions">
                        <common-button
                            :label="$t('pages.governance.detail.actions.run_round')"
                            size="small"
                            type="secondary"
                            :isDisabled="!canRunRound"
                            :isProcessing="isActing"
                            @click="runRound"
                        ></common-button>
                        <common-button
                            :label="$t('pages.governance.detail.actions.stop')"
                            size="small"
                            type="primary"
                            :isDisabled="!canStop"
                            :isProcessing="isActing"
                            @click="showStopConfirm = true"
                        ></common-button>
                    </common-buttons>

                    <section class="governance-section">
                        <h2 class="governance-section__title">{{ $t('pages.governance.detail.rounds.title') }}</h2>

                        <p v-if="!broadcast.rounds.length" class="governance-section__empty">
                            {{ $t('pages.governance.detail.rounds.empty') }}
                        </p>

                        <ol v-else class="governance-rounds">
                            <li v-for="round in broadcast.rounds" :key="round.round" class="governance-rounds__item">
                                <div class="governance-rounds__heading">
                                    <strong>{{ $t('pages.governance.detail.rounds.round', { round: round.round }) }}</strong>
                                    <span>
                                        {{ $t('pages.governance.detail.rounds.timeout', { minutes: round.timeoutMinutes }) }}
                                    </span>
                                    <span>
                                        {{ $t('pages.governance.detail.rounds.expires_at') }}: {{ formatDate(round.expiresAt) }}
                                    </span>
                                </div>

                                <p v-if="round.decision && round.decision.widthReason" class="governance-rounds__reason">
                                    {{ round.decision.widthReason }}
                                </p>

                                <p
                                    v-if="round.decision && (round.decision.suppressed || []).length"
                                    class="governance-rounds__suppressed"
                                >
                                    {{
                                        $t('pages.governance.detail.rounds.suppressed', {
                                            count: round.decision.suppressed.length,
                                        })
                                    }}
                                </p>

                                <div
                                    v-if="round.decision && (round.decision.providers || []).length"
                                    class="governance-round-providers"
                                >
                                    <div class="governance-round-providers__header">
                                        <div>{{ $t('pages.governance.detail.rounds.tier') }}</div>
                                        <div>{{ $t('pages.governance.detail.contacts.provider') }}</div>
                                        <div>{{ $t('pages.governance.detail.contacts.status') }}</div>
                                        <div>{{ $t('pages.governance.detail.contacts.score') }}</div>
                                        <div>{{ $t('pages.governance.detail.contacts.channels') }}</div>
                                    </div>
                                    <div
                                        v-for="provider in round.decision.providers"
                                        :key="provider.providerId"
                                        class="governance-round-providers__row"
                                    >
                                        <div>{{ provider.tier }}</div>
                                        <div>{{ provider.providerId }}</div>
                                        <div>{{ provider.status }}</div>
                                        <div>{{ provider.score }}</div>
                                        <div class="governance-contacts__channels">
                                            <span
                                                v-for="channel in provider.channels || []"
                                                :key="channel"
                                                class="governance-chip"
                                            >
                                                {{ channel }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ol>
                    </section>

                    <section class="governance-section">
                        <h2 class="governance-section__title">{{ $t('pages.governance.detail.contacts.title') }}</h2>

                        <p v-if="!broadcast.contacts.length" class="governance-section__empty">
                            {{ $t('pages.governance.detail.contacts.empty') }}
                        </p>

                        <div v-else class="governance-contacts">
                            <div class="governance-contacts__header">
                                <div>{{ $t('pages.governance.detail.contacts.provider') }}</div>
                                <div>{{ $t('pages.governance.detail.contacts.round') }}</div>
                                <div>{{ $t('pages.governance.detail.contacts.status') }}</div>
                                <div>{{ $t('pages.governance.detail.contacts.score') }}</div>
                                <div>{{ $t('pages.governance.detail.contacts.confidence') }}</div>
                                <div>{{ $t('pages.governance.detail.contacts.channels') }}</div>
                            </div>

                            <template v-for="contact in sortedContacts">
                                <div :key="`row-${contact.providerId}`" class="governance-contacts__row">
                                    <div>{{ contact.providerId }}</div>
                                    <div>{{ contact.round }}</div>
                                    <div>{{ contact.status }}</div>
                                    <div>{{ contact.score }}</div>
                                    <div>{{ contact.confidence }}%</div>
                                    <div class="governance-contacts__channels">
                                        <span v-for="channel in contact.channels || []" :key="channel" class="governance-chip">
                                            {{ channel }}
                                        </span>
                                    </div>
                                </div>
                                <details
                                    v-if="(contact.reasons || []).length"
                                    :key="`why-${contact.providerId}`"
                                    class="governance-contacts__why"
                                >
                                    <summary>{{ $t('pages.governance.detail.contacts.why') }}</summary>
                                    <ul>
                                        <li v-for="(reason, index) in contact.reasons" :key="index">{{ reason }}</li>
                                    </ul>
                                    <table v-if="(contact.breakdown || []).length" class="governance-breakdown">
                                        <tbody>
                                            <tr v-for="part in contact.breakdown" :key="part.key">
                                                <td>{{ part.key }}</td>
                                                <td>{{ part.value }}</td>
                                                <td>{{ Math.round(part.weight * 100) }}%</td>
                                                <td>{{ part.contribution }}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </details>
                            </template>
                        </div>
                    </section>

                    <section class="governance-section">
                        <h2 class="governance-section__title">{{ $t('pages.governance.detail.timeline.title') }}</h2>

                        <p v-if="!broadcast.timeline.length" class="governance-section__empty">
                            {{ $t('pages.governance.detail.timeline.empty') }}
                        </p>

                        <ol v-else class="governance-timeline">
                            <li v-for="(entry, index) in broadcast.timeline" :key="index" class="governance-timeline__item">
                                <span>
                                    {{
                                        $t('pages.governance.detail.timeline.transition', {
                                            from: entry.fromStatus || '—',
                                            to: entry.toStatus,
                                        })
                                    }}
                                </span>
                                <span class="governance-timeline__at">{{ formatDate(entry.at) }}</span>
                            </li>
                        </ol>
                    </section>
                </template>
            </common-content-spacer>
        </common-page-plain>

        <common-are-you-sure-modal
            v-if="showStopConfirm"
            :headingText="$t('pages.governance.detail.actions.stop')"
            :contentText="$t('pages.governance.detail.actions.stop_confirm')"
            :noButtonText="$t('common.microcopy.cancel')"
            :yesButtonText="$t('common.microcopy.confirm')"
            showWarningIcon
            @close="showStopConfirm = false"
            @continue="stop"
        ></common-are-you-sure-modal>
    </app-layout>
</template>

<script>
    import AppLayout from 'components/layouts/AppLayout'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import GovernanceStatusBadge from 'components/page-elements/governance/GovernanceStatusBadge'
    import GovernanceAPI from 'api/GovernanceAPI'
    import BroadcastStatusEnum from 'enums/BroadcastStatusEnum'
    import dayjs from 'dayjs'

    const STOPPABLE_STATUSES = [BroadcastStatusEnum.BROADCASTING, BroadcastStatusEnum.WAITING, BroadcastStatusEnum.EXPANDING]
    const ROUND_ACTIONABLE_STATUSES = [BroadcastStatusEnum.BROADCASTING, BroadcastStatusEnum.WAITING]

    export default {
        mixins: [PageWithTitleMixin],

        components: {
            AppLayout,
            GovernanceStatusBadge,
        },

        data() {
            return {
                isLoading: false,
                hasError: false,
                isActing: false,
                broadcast: null,
                showStopConfirm: false,
            }
        },

        computed: {
            pageTitle() {
                return this.broadcast ? `${this.$t('pages.governance.detail.title')} #${this.broadcast.id}` : ''
            },

            canStop() {
                return this.broadcast && STOPPABLE_STATUSES.includes(this.broadcast.status)
            },

            canRunRound() {
                return this.broadcast && ROUND_ACTIONABLE_STATUSES.includes(this.broadcast.status)
            },

            sortedContacts() {
                if (!this.broadcast) return []
                return [...this.broadcast.contacts].sort((a, b) => a.round - b.round || b.score - a.score)
            },
        },

        methods: {
            load() {
                this.isLoading = true
                this.hasError = false

                GovernanceAPI.getByReferralId(this.$route.params.referralId)
                    .then((response) => {
                        this.broadcast = response
                    })
                    .catch(() => {
                        this.hasError = true
                        this.broadcast = null
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            runAction(promise) {
                this.isActing = true
                return promise
                    .then(() => this.load())
                    .catch(() => {
                        this.hasError = true
                    })
                    .finally(() => {
                        this.isActing = false
                    })
            },

            stop() {
                this.showStopConfirm = false
                this.runAction(GovernanceAPI.stop(this.broadcast.id))
            },

            runRound() {
                this.runAction(GovernanceAPI.runRound(this.broadcast.id))
            },

            formatDate(value) {
                return value ? dayjs(value).format('MMM D, YYYY h:mm A') : '—'
            },
        },

        mounted() {
            this.load()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables';

    .governance-detail__back {
        display: inline-block;
        margin-bottom: 16px;
        color: $color--txt-base-2;
        text-decoration: none;
        font-size: 14px;

        &:hover {
            text-decoration: underline;
        }
    }

    .governance-detail__state {
        padding: 3rem 1rem;
        text-align: center;
        color: $color--txt-base-3;

        &--error {
            color: $color--accent-8;
        }
    }

    .governance-summary {
        display: flex;
        gap: 32px;
        margin: 16px 0 24px;
    }

    .governance-summary__label {
        margin: 0 0 4px;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: $color--txt-base-3;
    }

    .governance-summary__value {
        margin: 0;
        font-size: 18px;
        font-weight: 700;
    }

    .governance-actions {
        margin-bottom: 32px;
    }

    .governance-section {
        margin-bottom: 32px;
    }

    .governance-section__title {
        margin: 0 0 12px;
        font-size: 18px;
        font-weight: 700;
    }

    .governance-section__empty {
        color: $color--txt-base-3;
    }

    .governance-rounds {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .governance-rounds__item {
        padding: 12px 16px;
        border: 1px solid $color--border-base;
        border-radius: 8px;
        margin-bottom: 8px;
    }

    .governance-rounds__heading {
        display: flex;
        gap: 16px;
        flex-wrap: wrap;
        font-size: 14px;
    }

    .governance-rounds__reason {
        margin: 8px 0 0;
        font-size: 13px;
        color: $color--txt-base-2;
    }

    .governance-rounds__suppressed {
        margin: 4px 0 0;
        font-size: 13px;
        color: $color--accent-15;
    }

    .governance-round-providers {
        margin-top: 12px;
        border: 1px solid $color--border-base;
        border-radius: 6px;
        overflow: hidden;
    }

    .governance-round-providers__header,
    .governance-round-providers__row {
        display: grid;
        grid-template-columns: 0.5fr 0.8fr 0.8fr 0.5fr 1fr;
        gap: 1rem;
        align-items: center;
        padding: 0.5rem 0.75rem;
        font-size: 13px;
    }

    .governance-round-providers__header {
        font-weight: bold;
        background: $color--bg-dropdown;
    }

    .governance-round-providers__row:not(:last-child) {
        border-bottom: 1px solid $color--border-base;
    }

    .governance-contacts__header,
    .governance-contacts__row {
        display: grid;
        grid-template-columns: 0.8fr 0.5fr 0.9fr 0.5fr 0.7fr 1fr;
        gap: 1rem;
        align-items: center;
    }

    .governance-contacts__header {
        padding: 0.75rem 1rem;
        font-weight: bold;
        background: $color--bg-dropdown;
    }

    .governance-contacts__row {
        padding: 0.6rem 1rem;
        border-bottom: 1px solid $color--border-base;
    }

    .governance-contacts__channels {
        display: flex;
        gap: 4px;
        flex-wrap: wrap;
    }

    .governance-chip {
        padding: 1px 8px;
        border-radius: 10px;
        background: $color--bg-dropdown;
        border: 1px solid $color--border-base;
        font-size: 11px;
    }

    .governance-contacts__why {
        padding: 0 1rem 0.75rem 2rem;
        border-bottom: 1px solid $color--border-base;
        font-size: 13px;
        color: $color--txt-base-2;

        summary {
            cursor: pointer;
            color: $color--accent-3;
        }

        ul {
            margin: 8px 0;
            padding-left: 18px;
        }
    }

    .governance-breakdown {
        width: 100%;
        max-width: 420px;
        border-collapse: collapse;
        font-size: 12px;

        td {
            padding: 4px 8px;
            border-bottom: 1px solid $color--border-base;
        }
    }

    .governance-timeline {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .governance-timeline__item {
        display: flex;
        justify-content: space-between;
        padding: 8px 0;
        border-bottom: 1px solid $color--border-base;
        font-size: 14px;
    }

    .governance-timeline__at {
        color: $color--txt-base-3;
        font-size: 12px;
    }
</style>
